import React from 'react';
import { arrowWidth } from '@/components/navigation-arrows';
import { Button } from '@/components/button';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { openWebBrowserURL } from '@/utils/navigation';
import { SCREEN_PADDING_SMALL } from '@/styles';
import { ScrollView, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';

export const HERO_HEIGHT = 280;

const ARROW_CLEARANCE = arrowWidth + 2 * SCREEN_PADDING_SMALL;

export type TakeoverLayout = 'mobile' | 'web';

interface Props {
  body: string;
  callToActionLabel: string | null;
  callToActionUrl: string | null;
  clearArrows?: boolean;
  heroImageUrl: string | null;
  layout: TakeoverLayout;
  onCallToAction: () => void;
  title: string;
  uuid: string;
}

export const LandingPageTakeoverContent: React.FC<Props> = ({
  uuid,
  body,
  callToActionLabel,
  callToActionUrl,
  clearArrows = false,
  heroImageUrl,
  layout,
  onCallToAction,
  title,
}) => {
  const [styles, theme] = useStyles();

  const isFullScreen = layout === 'mobile';
  const inset = clearArrows
    ? styles.arrowClearance
    : isFullScreen
      ? styles.insetMobile
      : styles.insetWeb;

  const hero = heroImageUrl ? (
    <CachedImage
      accessibilityIgnoresInvertColors
      alt={title}
      contentFit="cover"
      source={{ uri: heroImageUrl }}
      style={[styles.hero, isFullScreen ? null : styles.heroFramed]}
      testID="landing-page-takeover-hero"
    />
  ) : null;

  return (
    <View style={isFullScreen ? styles.fill : styles.stretch}>
      <ScrollView style={styles.stretch}>
        {isFullScreen ? hero : null}
        <View
          style={[isFullScreen ? styles.pageMobile : styles.pageWeb, inset]}
          testID="landing-page-takeover-text-block"
        >
          <Typography
            color={theme.sk.color.text.primary}
            testID="landing-page-takeover-title"
            variant="heading-xl"
          >
            {title}
          </Typography>
          {isFullScreen ? null : hero}
          <Typography
            color={theme.sk.color.text.secondary}
            testID="landing-page-takeover-body"
            variant="paragraph-base"
          >
            {body}
          </Typography>
        </View>
      </ScrollView>
      {callToActionLabel && callToActionUrl ? (
        <View
          style={[isFullScreen ? styles.footerMobile : styles.footerWeb, inset]}
          testID="landing-page-takeover-footer"
        >
          <Button
            onPress={() => {
              onCallToAction();
              void openWebBrowserURL(callToActionUrl);
            }}
            testID="landing-page-takeover-cta"
            trackingData={{
              name: 'landing_page_takeover.cta',
              properties: {
                landingPageUUID: uuid,
              },
            }}
            type="primary"
          >
            {callToActionLabel}
          </Button>
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  arrowClearance: {
    paddingHorizontal: ARROW_CLEARANCE,
  },
  fill: {
    flex: 1,
  },
  footerMobile: {
    paddingBottom: theme.sk.spacing.spaceLarge,
  },
  footerWeb: {
    paddingBottom: theme.sk.spacing.spaceXlarge,
  },
  hero: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    height: HERO_HEIGHT,
    width: '100%',
  },
  heroFramed: {
    borderRadius: theme.sk.radii.radiusMedium,
  },
  insetMobile: {
    paddingHorizontal: theme.sk.spacing.spaceLarge,
  },
  insetWeb: {
    paddingHorizontal: theme.sk.spacing.spaceXlarge,
  },
  pageMobile: {
    gap: theme.sk.spacing.spaceMedium,
    paddingBottom: theme.sk.spacing.spaceLarge,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  pageWeb: {
    gap: theme.sk.spacing.spaceLarge,
    paddingBottom: theme.sk.spacing.spaceLarge,
    paddingTop: 0,
  },
  stretch: {
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
  },
}));
